import React, { useState, useEffect, useCallback ,useMemo} from 'react';
import Slider from 'react-slick';
import './Home.css';
import { FaLeaf, FaFlask, FaClock, FaTruck, FaShieldAlt, FaSeedling } from 'react-icons/fa';

import farmVideo from '../assets/farm-video.mp4';

import farmVideo1 from '../assets/video1.mp4';
import farmVideo2 from '../assets/video2.mp4';

import productImage1 from '../assets/fertilizer.jpg';
import productImage2 from '../assets/fertilizer1.jpg';
import productImage3 from '../assets/fertilizer3.jpg';
import productImage4 from '../assets/fertlizer2.jpg';
import productImage from '../assets/fertlizer4.jpg';
import productImage6 from '../assets/fertilizer6.jpg';

import productImage7 from '../assets/fertlizer7.jpg';
import productImage8 from '../assets/fertilizer8.jpg';
import productImage9 from '../assets/fertilizer9.jpg';
import productImage11 from '../assets/fertilizer11.jpg';
import productImage12 from '../assets/fertilizer12.jpg';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  
  const [isVisible, setIsVisible] = useState({
    features: false,
    products: false,
    video: false,
    stats: false
  });

  // Counter animation for statistics
  const [counters, setCounters] = useState({
    farmers: 0,
    acresCovered: 0,
    products: 0,
    satisfaction: 0
  });

  const finalCounters = useMemo(() => ({
    farmers: 100000,
    acresCovered: 300000,
    products: 255,
    satisfaction: 98
  }), []);
  

  // Use useCallback to memoize the animateCounters function
  const animateCounters = useCallback(() => {
    const duration = 2000; // 2 seconds
    const steps = 50;
    const interval = duration / steps;

    let step = 0;
    
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      
      setCounters({
        farmers: Math.floor(finalCounters.farmers * progress),
        acresCovered: Math.floor(finalCounters.acresCovered * progress),
        products: Math.floor(finalCounters.products * progress),
        satisfaction: Math.floor(finalCounters.satisfaction * progress)
      });

      if (step >= steps) {
        clearInterval(timer);
      }
    }, interval);
  }, [finalCounters]); // Adding finalCounters as a dependency

  // Intersection Observer for animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: "0px 0px -100px 0px"
    };

    const handleIntersection = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.dataset.section]: true
          }));
          
          // Start counter animation when stats section is visible
          if (entry.target.dataset.section === 'stats') {
            animateCounters();
          }
          
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);
    
    const sections = document.querySelectorAll('[data-section]');
    sections.forEach(section => {
      observer.observe(section);
    });

    return () => {
      sections.forEach(section => {
        observer.unobserve(section);
      });
    };
  }, [animateCounters]); // Include animateCounters in dependency array

  // Handle input changes
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value,
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    const { name, email, message } = formData;

    // Construct the WhatsApp message
    const waMessage = `Hello, I need help with the following:\n\nName: ${name}\nEmail: ${email}\nMessage: ${message}`;

    const phoneNumber = '7350552704'; // Random number

    // WhatsApp link with the country code and the message
    const waURL = `https://wa.me/91${phoneNumber}?text=${encodeURIComponent(waMessage)}`;

    // Open WhatsApp with the constructed message
    window.open(waURL, "_blank");
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 3000,
    slidesToShow: 3,
    slidesToScroll: 1,
    centerMode: true,
    focusOnSelect: true,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 600, settings: { slidesToShow: 1 } }
    ]
  };

  return (
    <div className="home-container">
      {/* Hero Section */}
      <div className="hero-container">
        <video autoPlay muted loop className="hero-video">
          <source src={farmVideo2} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="overlay"></div>
        <section className="hero-section">
          <div className="hero-content">
            <div className="logo-highlight">
              <div className="leaf-icon"><FaLeaf /></div>
            </div>
            <h1 className="hero-title">Welcome to Hygenic Agro</h1>
            <p className="hero-subtitle">High-Quality Fertilizers for a Greener Tomorrow</p>
            <div className="hero-buttons">
              <a href="/products" className="hero-button primary">Explore Fertilizers</a>
              <a href="#contact" className="hero-button secondary">Contact Us</a>
            </div>
            <div className="scroll-indicator">
              
              
            </div>
          </div>
        </section>
      </div>

      {/* Quick Stats Banner */}
      <section className="stats-banner" data-section="stats">
        <div className="stats-container">
          <div className="stat-box">
            <div className="stat-icon"><FaSeedling /></div>
            <div className="stat-number">{counters.farmers}+</div>
            <div className="stat-label">Happy Farmers</div>
          </div>
          <div className="stat-box">
            <div className="stat-icon"><FaShieldAlt /></div>
            <div className="stat-number">{counters.acresCovered}+</div>
            <div className="stat-label">Acres Covered</div>
          </div>
          <div className="stat-box">
            <div className="stat-icon"><FaFlask /></div>
            <div className="stat-number">{counters.products}+</div>
            <div className="stat-label">Products</div>
          </div>
          <div className="stat-box">
            <div className="stat-icon"><FaLeaf /></div>
            <div className="stat-number">{counters.satisfaction}%</div>
            <div className="stat-label">Satisfaction</div>
          </div>
        </div>
      </section>

      {/* Fertilizer Products Carousel */}
      <section className="products-carousel-section" data-section="products">
        <h2 className="section-title">Our Fertilizer Products</h2>
        <p className="section-subtitle">Premium quality for maximum yield</p>
        <Slider {...settings}>
          <div className="carousel-item">
            <div className="product-tag">Bestseller</div>
            <img src={productImage} alt="Greehexa CHIORO 26" />
            <h3>Greehexa CHIORO 26</h3>
            <p>Complete Npk+TE Nutrition.</p>
            <p>Fruit Tree Vegetables.</p>
           {/* <a href="/products/chioro26" className="product-link"></a> */}
          </div>
          <div className="carousel-item">
            <div className="product-tag">New</div>
            <img src={productImage1} alt="Greehexa Growth" />
            <h3>Greehexa Growth</h3>
            <p>Complete NPK+Zinc+Borown+Pgp Nutrition.</p>
            <p>Grapes Fruit Trees Vegetables.</p>
           
          </div>
          
          <div className="carousel-item">
            <div className="product-tag">Premium</div>
            <img src={productImage6} alt="Greehexa Boost" />
            <h3>Greehexa Boost</h3>
            <p>Complete NPK+TE fe Nutrition.</p>
            <p>Feild Crops Vegetables Crops</p>
            
            
          </div>
          
          <div className="carousel-item">
            <div className="product-tag">Bestseller</div>
            <img src={productImage8} alt="Greehexa CZB" />
            <h3>Greehexa CZB</h3>
            <p>Calcium ,Zinc Boron Nutrition.</p>
            <p>Fruit Tree Vegetables.</p>
           
          </div>
           <div className="carousel-item">
            <div className="product-tag">Bestseller</div>
            <img src={productImage11} alt="Greehexa CZB" />
            <h3>FERO-P</h3>
            <p>Fe-HEDP (Fe-17%).</p>
            <p>A product of plant science division.</p>
           
          </div>
           <div className="carousel-item">
            <div className="product-tag">Bestseller</div>
            <img src={productImage12} alt="Greehexa CZB" />
            <h3>ZENKO-P</h3>
           <p>Zn-HEDP (Zn-17%).</p>
            <p>A product of plant science division.</p>
           
          </div>
          
        </Slider>
      </section>

      {/* Features Section */}
      <section className="features-section" data-section="features">
        <div className="section-header">
          <h2 className="section-title">Why Choose Hygenic Agro?</h2>
          <p className="section-subtitle">We deliver quality that farmers trust</p>
        </div>
        <div className={`features ${isVisible.features ? 'features-visible' : ''}`}>
          <div className="feature-card">
            <div className="feature-icon"><FaFlask size={40} /></div>
            <h3>Lab-Tested Quality</h3>
            <p>All products are lab-tested and certified for agricultural safety.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><FaLeaf size={40} /></div>
            <h3>High-Quality Fertilizers</h3>
            <p>Our fertilizers are formulated to promote healthy crops and soil fertility.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><FaClock size={40} /></div>
            <h3>Quick-Acting Fertilizers</h3>
            <p>Designed to deliver quick nutrients to plants, ensuring fast growth and results.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><FaTruck size={40} /></div>
            <h3>Fast Delivery</h3>
            <p>We ensure timely delivery to all locations, keeping your farm schedule on track.</p>
          </div>
        </div>
      </section>

      {/* Crop Solutions Grid */}
      <section className="crop-solutions-section">
        <div className="section-header">
          <h2 className="section-title">Solutions For Every Crop</h2>
          <p className="section-subtitle">Specialized fertilizers for different agricultural needs</p>
        </div>
        <div className="crop-grid">
          <div className="crop-card">
            <div className="crop-image rice"></div>
            <h3>Rice</h3>
            <p>Specialized solutions for paddy fields</p>
          </div>
          <div className="crop-card">
            <div className="crop-image wheat"></div>
            <h3>Wheat</h3>
            <p>Boost wheat yields with our targeted formula</p>
          </div>
          <div className="crop-card">
            <div className="crop-image vegetables"></div>
            <h3>Vegetables</h3>
            <p>For healthy, nutritious vegetable crops</p>
          </div>
          <div className="crop-card">
            <div className="crop-image fruits"></div>
            <h3>Fruits</h3>
            <p>Enhance fruit quality and quantity</p>
          </div>
        </div>
      </section>

      {/* Fertilizer Info Video Section */}
      <section className="fertilizer-video-section" data-section="video">
        <div className={`video-content-wrapper ${isVisible.video ? 'video-visible' : ''}`}>
          <div className="video-text-content">
            <div className="section-badge">WATCH & LEARN</div>
            <h2>See Our Fertilizers in Action</h2>
            <p>
              Discover how our eco-friendly fertilizers enhance crop health and productivity.
              This video gives you a close-up look at how our products are applied and the results they deliver.
            </p>
            <ul className="video-highlights">
              <li><FaLeaf className="highlight-icon" /> Eco-friendly application methods</li>
              <li><FaLeaf className="highlight-icon" /> Real results from real farms</li>
              <li><FaLeaf className="highlight-icon" /> Expert tips for maximum effectiveness</li>
            </ul>
          </div>
          <div className="video-player-container">
            <div className="video-player">
              <div className="play-button-overlay">
                <div className="play-button"></div>
              </div>
              <video controls poster={require('../assets/img.jpg')}>
                <source src={farmVideo1} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-section" id="contact">


        <div className="contact-wrapper">
          <div className="contact-left">
            <h2 className="section-title">Contact Us</h2>
            <p className="section-description">Got a question or need help? Reach out to us and we'll get back to you soon.</p>
            <div className="contact-info">
              <div className="contact-item">
                <div className="contact-icon">📍</div>
                <div className="contact-text">
                  <h4>Our Location</h4>
                  <p>Office no -7140, Floor no-07, Marvel Fuego, Opp. Seasons Mall, Magarpatta Road, Hadapsar, Pune, Maharashtra,-411028. 
              </p>
                  <p>Singapore Address: Suntec Tower Three, Level 42, Suntec Tower Three8 Temasek Boulevard, Singapore 038988</p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">📞</div>
                <div className="contact-text">
                  <h4>Phone Number</h4>
                  <p>+91 7350552704</p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">✉️</div>
                <div className="contact-text">
                  <h4>Email Address</h4>
                  <p>hygenicagro123@gmail.com
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="contact-right">
            <div className="form-container">
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-header">
                  <h3>Send us a Message</h3>
                  <p>We'll respond within 24 hours</p>
                </div>
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    placeholder="Enter your full name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter your email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Your Message</label>
                  <textarea
                    id="message"
                    rows="6"
                    placeholder="Write your message here..."
                    required
                    value={formData.message}
                    onChange={handleChange}
                  ></textarea>
                </div>
                <button type="submit" className="submit-button">
                  <span>Send Message</span>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 2L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </form>
            </div>
          </div>
        </div>
        <div className="wave-bottom">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
            <path fill="#4caf50" fillOpacity="0.2" d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,218.7C672,235,768,245,864,229.3C960,213,1056,171,1152,149.3C1248,128,1344,128,1392,128L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
      </section>
    </div>
  );
};

export default Home;